import React, { useState, useEffect, useCallback } from 'react';
import { Table, message, Modal, Alert, Button } from 'antd';
import { getAllProducts, deleteProduct, createProduct, updateProduct } from '../../Utils/products/products';
import Header from '../../components/Shared/Header';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ProductPopUp from '../../components/Products/ProductPopUp';

const ProductsPage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditingProduct, setIsEditingProduct] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getAllProducts();
            setProducts(response.data);
        } catch (error) {
            setError(error.message || 'Failed to fetch products');
            message.error('Failed to fetch products');
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const getThemes = useCallback((product) => {
        if (!product) return 'No themes';
        
        const themes = [];
        if (product.is_irrigation) themes.push('Irrigation');
        if (product.is_soil) themes.push('Sol');
        return themes.length ? themes.join(', ') : 'No themes';
    }, []);

    const handleAddProduct = () => {
        setIsEditingProduct(null);
        setIsModalVisible(true);
    };

    const handleEditProduct = (product) => {
        setIsEditingProduct(product);
        setIsModalVisible(true);
    };

    const handleDeleteProduct = useCallback(async (productId) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this product?',
            content: 'This action cannot be undone.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    await deleteProduct(productId);
                    message.success('Product deleted successfully');
                    await fetchProducts();
                    return true;
                } catch (error) {
                    message.error('Failed to delete product');
                    console.error('Error deleting product:', error);
                    return false;
                }
            },
        });
    }, [fetchProducts]);

    const handleProductOperation = useCallback(async (operation, values, productId = null) => {
        try {
            setError(null);
            if (operation === 'create') {
                await createProduct(values);
                message.success('Product created successfully');
            } else if (operation === 'update' && productId) {
                await updateProduct(productId, values);
                message.success('Product updated successfully');
            }
            await fetchProducts();
            setIsModalVisible(false);
            return true;
        } catch (error) {
            const errorMsg = `Failed to ${operation} product`;
            message.error(errorMsg);
            setError(error.message || errorMsg);
            console.error(`Error ${operation}ing product:`, error);
            return false;
        }
    }, [fetchProducts]);

    const columns = [
        {
            title: 'Product Name',
            dataIndex: 'product_name',
            key: 'product_name',
            sorter: (a, b) => a.product_name.localeCompare(b.product_name)
        },
        {
            title: 'Creation Date',
            dataIndex: 'creation_date',
            key: 'creation_date',
            render: (date) => new Date(date).toLocaleDateString(),
            sorter: (a, b) => new Date(a.creation_date) - new Date(b.creation_date)
        },
        {
            title: 'Theme(s)',
            key: 'themes',
            render: (_, record) => getThemes(record),
            filters: [
                { text: 'Irrigation', value: 'irrigation' },
                { text: 'Sol', value: 'soil' }
            ],
            onFilter: (value, record) => 
                value === 'irrigation' ? record.is_irrigation : record.is_soil
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEditProduct(record)}
                    />
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteProduct(record.product_id)}
                    />
                </div>
            ),
        },
    ];

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
            <Header title="Products List" />
            
            {error && (
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    style={{ margin: '16px' }}
                    closable
                />
            )}

            <div style={{ padding: '24px' }}>
                <Table
                    dataSource={products}
                    columns={columns}
                    rowKey="product_id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} items`,
                        showQuickJumper: true
                    }}
                    style={{ 
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
                        borderRadius: '8px' 
                    }}
                />

                <ProductPopUp
                    visible={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    onSubmit={(values) => {
                        const operation = isEditingProduct ? 'update' : 'create';
                        return handleProductOperation(
                            operation,
                            values,
                            isEditingProduct?.product_id
                        );
                    }}
                    initialValues={isEditingProduct}
                />

                <Button
                    type="primary"
                    shape="circle"
                    icon={<PlusOutlined />}
                    size="large"
                    onClick={handleAddProduct}
                    style={{
                        position: 'fixed',
                        bottom: '40px',
                        right: '40px',
                        width: '64px',
                        height: '64px',
                        fontSize: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                    }}
                />
            </div>
        </div>
    );
};

export default ProductsPage;